// Movie API service for handling Supabase integration
import { supabase } from '../lib/supabase';

// Structure to match Google Apps Script output
export const movieDataStructure = {
  videoId: 'string',
  channelName: 'string', 
  title: 'string',
  publishedDate: 'string',
  description: 'string',
  viewCount: 'number',
  duration: 'string',
  embedCode: 'string'
};

// Convert Google Sheets row to our video format
export const convertSheetRowToVideo = (row) => {
  const [videoId, channelName, title, publishedDate, description, viewCount, duration, embedCode] = row;
  
  return {
    id: videoId,
    title: title,
    category: 'Curated Collections', // Default category, can be enhanced
    year: new Date(publishedDate).getFullYear(),
    duration: duration,
    views: formatViewCount(viewCount),
    channelName: channelName,
    channelUrl: generateChannelUrl(channelName), // Generate YouTube channel URL
    publishedDate: publishedDate,
    description: description,
    embedCode: embedCode
  };
};

// Generate YouTube channel URL from channel name
const generateChannelUrl = (channelName) => {
  if (!channelName) return null;
  
  // Common Nollywood channel URL patterns
  const channelUrlMap = {
    'Nollywood Central': 'https://www.youtube.com/@NollywoodCentral',
    'Nollywood Movies': 'https://www.youtube.com/@NollywoodMovies',
    'Nollywood TV': 'https://www.youtube.com/@NollywoodTV',
    'Nollywood Forever': 'https://www.youtube.com/@NollywoodForever',
    'Nollywood Classics': 'https://www.youtube.com/@NollywoodClassics',
    'Nollywood Latest': 'https://www.youtube.com/@NollywoodLatest',
    'Nollywood Studios': 'https://www.youtube.com/@NollywoodStudios',
    'Nollywood Entertainment': 'https://www.youtube.com/@NollywoodEntertainment',
    'Nollywood World': 'https://www.youtube.com/@NollywoodWorld',
    'Nollywood Cinema': 'https://www.youtube.com/@NollywoodCinema'
  };
  
  // Check if we have a direct mapping
  if (channelUrlMap[channelName]) {
    return channelUrlMap[channelName];
  }
  
  // For unknown channels, create a search URL as fallback
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(channelName + ' official channel')}`;
};

const MOVIE_BASE_FIELDS =
  'video_id, title, channel_name, published_date, description, view_count, duration, embed_code';

const MOVIE_WITH_CATEGORIES = `${MOVIE_BASE_FIELDS}, movie_categories ( category_id, categories ( id, name, slug, color ) )`;

/** Public homepage respects Admin "Publish" flags; missing table/error = show curated content. */
async function getHomepagePublishFlags() {
  try {
    const { data, error } = await supabase
      .from('site_homepage_settings')
      .select('slider_published, categories_published')
      .eq('id', 1)
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      return { sliderPublished: false, categoriesPublished: false };
    }
    return {
      sliderPublished: data.slider_published === true,
      categoriesPublished: data.categories_published === true
    };
  } catch {
    return { sliderPublished: true, categoriesPublished: true };
  }
}

function normalizeCategoriesFromRow(movie) {
  const mc = movie.movie_categories;
  if (!Array.isArray(mc)) return [];
  return mc
    .map((junction) => junction.categories)
    .filter(Boolean)
    .map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
      color: c.color
    }));
}

function mapDbMovieToFrontend(movie) {
  const categories = normalizeCategoriesFromRow(movie);
  const primaryCategory = categories[0]?.name || 'Curated Collections';
  return {
    id: movie.video_id,
    title: movie.title,
    category: primaryCategory,
    categories,
    year: new Date(movie.published_date).getFullYear(),
    duration: movie.duration,
    views: formatViewCount(movie.view_count),
    channelName: movie.channel_name,
    channelUrl: generateChannelUrl(movie.channel_name),
    publishedDate: movie.published_date,
    description: movie.description,
    embedCode: movie.embed_code
  };
}

// Format view count for display
const formatViewCount = (count) => {
  const num = parseInt(count);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Validate YouTube video ID
export const isValidYouTubeId = (id) => {
  if (!id || typeof id !== 'string') return false;
  // YouTube video IDs are typically 11 characters and contain alphanumeric characters, hyphens, and underscores
  const youtubeIdPattern = /^[a-zA-Z0-9_-]{11}$/;
  return youtubeIdPattern.test(id);
};

// Fetch movies from Supabase with optimized query
export const fetchMovies = async (limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('movies')
      .select(MOVIE_WITH_CATEGORIES)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    console.log('✅ Fetched movies from Supabase:', data?.length || 0);

    return data
      ? data
          .filter((movie) => isValidYouTubeId(movie.video_id))
          .map(mapDbMovieToFrontend)
      : [];
  } catch (error) {
    console.error('❌ Error fetching movies from Supabase:', error);
    return [];
  }
};

// Fetch movies by channel name from Supabase
export const fetchMoviesByChannel = async (channelName) => {
  try {
    if (!channelName) return [];
    
    const decodedChannelName = decodeURIComponent(channelName);
    
    // Query Supabase with case-insensitive filter for channel name
    // Using ilike for case-insensitive pattern matching
    const { data, error } = await supabase
      .from('movies')
      .select(MOVIE_WITH_CATEGORIES)
      .ilike('channel_name', `%${decodedChannelName}%`)
      .order('published_date', { ascending: false });

    if (error) throw error;

    console.log(`✅ Fetched movies for channel "${decodedChannelName}":`, data?.length || 0);

    return data
      ? data
          .filter((movie) => isValidYouTubeId(movie.video_id))
          .map(mapDbMovieToFrontend)
      : [];
  } catch (error) {
    console.error('❌ Error fetching movies by channel from Supabase:', error);
    return [];
  }
};

// Get most watched movies from last 24 hours
export const getMostWatchedMovies = (movies, limit = 3) => {
  const now = new Date();
  const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  
  return movies
    .filter(movie => {
      const publishedDate = new Date(movie.publishedDate);
      return publishedDate >= last24Hours;
    })
    .sort((a, b) => {
      const aViews = parseInt(a.views.replace(/[KM]/g, '')) * (a.views.includes('M') ? 1000000 : 1000);
      const bViews = parseInt(b.views.replace(/[KM]/g, '')) * (b.views.includes('M') ? 1000000 : 1000);
      return bViews - aViews;
    })
    .slice(0, limit);
};

// Get most recent movies (last 24 hours)
export const getMostRecentMovies = (movies, limit = 3) => {
  const now = new Date();
  const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  
  return movies
    .filter(movie => {
      const publishedDate = new Date(movie.publishedDate);
      return publishedDate >= last24Hours;
    })
    .sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate))
    .slice(0, limit);
};

// Get randomized movies from different channels for hero section
export const getRandomizedHeroMovies = (movies, limit = 3) => {
  if (movies.length === 0) return [];
  
  // Group movies by channel
  const channelGroups = {};
  movies.forEach(movie => {
    const channel = movie.channelName || 'Unknown Channel';
    if (!channelGroups[channel]) {
      channelGroups[channel] = [];
    }
    channelGroups[channel].push(movie);
  });
  
  const channels = Object.keys(channelGroups);
  const selectedMovies = [];
  
  // First, try to get one movie from each channel
  channels.forEach(channel => {
    if (selectedMovies.length < limit && channelGroups[channel].length > 0) {
      const randomIndex = Math.floor(Math.random() * channelGroups[channel].length);
      selectedMovies.push(channelGroups[channel][randomIndex]);
    }
  });
  
  // If we need more movies and have channels with multiple movies, add more
  while (selectedMovies.length < limit) {
    const randomChannel = channels[Math.floor(Math.random() * channels.length)];
    if (channelGroups[randomChannel].length > 0) {
      const randomIndex = Math.floor(Math.random() * channelGroups[randomChannel].length);
      const movie = channelGroups[randomChannel][randomIndex];
      
      // Avoid duplicates
      if (!selectedMovies.find(m => m.id === movie.id)) {
        selectedMovies.push(movie);
      }
    }
    
    // Prevent infinite loop
    if (selectedMovies.length === movies.length) break;
  }
  
  // Shuffle the selected movies
  return selectedMovies.sort(() => Math.random() - 0.5);
};

function createCollectionsFromDbCategories(movies) {
  const byId = new Map();
  movies.forEach((m) => {
    (m.categories || []).forEach((c) => {
      if (c && c.id && !byId.has(c.id)) byId.set(c.id, c);
    });
  });
  const ordered = [...byId.values()].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
  const collections = {};
  ordered.forEach((c) => {
    collections[c.name] = movies.filter((m) =>
      (m.categories || []).some((x) => x && x.id === c.id)
    );
  });
  return collections;
}

/** Heuristic buckets when no DB categories are assigned */
function createCollectionsHeuristic(movies) {
  const collections = {
    'Trending Nollywood': [],
    'Most Watched': [],
    'New Releases': [],
    Romance: [],
    Comedy: [],
    Drama: []
  };

  const mostWatched = [...movies].sort((a, b) => {
    const aViews =
      parseInt(a.views.replace(/[KM]/g, ''), 10) *
      (a.views.includes('M') ? 1000000 : 1000);
    const bViews =
      parseInt(b.views.replace(/[KM]/g, ''), 10) *
      (b.views.includes('M') ? 1000000 : 1000);
    return bViews - aViews;
  });

  movies.forEach((movie) => {
    const title = movie.title.toLowerCase();
    const description = (movie.description || '').toLowerCase();

    if (mostWatched.indexOf(movie) < 20) {
      collections['Most Watched'].push(movie);
    }

    const publishedDate = new Date(movie.publishedDate);
    const last30Days = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    if (publishedDate >= last30Days) {
      collections['New Releases'].push(movie);
    }

    if (
      title.includes('love') ||
      title.includes('romance') ||
      title.includes('wedding') ||
      description.includes('love') ||
      description.includes('romance')
    ) {
      collections.Romance.push(movie);
    }

    if (
      title.includes('comedy') ||
      title.includes('funny') ||
      title.includes('laugh') ||
      description.includes('comedy') ||
      description.includes('funny')
    ) {
      collections.Comedy.push(movie);
    }

    if (
      title.includes('drama') ||
      title.includes('story') ||
      title.includes('life') ||
      description.includes('drama') ||
      description.includes('story')
    ) {
      collections.Drama.push(movie);
    }
  });

  collections['Trending Nollywood'] = getRandomizedHeroMovies(movies, 20);

  return collections;
}

/**
 * Homepage rows: built only from the admin `categories` table (order by name).
 * When the table has at least one category, this replaces heuristic buckets entirely.
 */
export async function fetchHomepageCollectionsFromAdmin() {
  try {
    const flags = await getHomepagePublishFlags();
    if (!flags.categoriesPublished) {
      return { adminMode: false, collections: {} };
    }

    const { data, error } = await supabase
      .from('categories')
      .select(
        `
        id,
        name,
        slug,
        color,
        movie_categories (
          movies ( ${MOVIE_BASE_FIELDS} )
        )
      `
      )
      .order('name', { ascending: true });

    if (error) throw error;

    if (!data?.length) {
      return { adminMode: false, collections: {} };
    }

    const collections = {};

    data.forEach((cat) => {
      const rawMovies = (cat.movie_categories || [])
        .map((mc) => mc.movies)
        .filter(Boolean)
        .filter((m) => isValidYouTubeId(m.video_id));

      rawMovies.sort(
        (a, b) =>
          new Date(b.published_date || 0) - new Date(a.published_date || 0)
      );

      const mapped = rawMovies.map((m) =>
        mapDbMovieToFrontend({
          ...m,
          movie_categories: [
            {
              categories: {
                id: cat.id,
                name: cat.name,
                slug: cat.slug,
                color: cat.color
              }
            }
          ]
        })
      );

      if (mapped.length > 0) {
        collections[cat.name] = mapped;
      }
    });

    return { adminMode: true, collections };
  } catch (e) {
    console.warn('Admin categories collections fetch failed, will use fallback:', e);
    return { adminMode: false, collections: {} };
  }
}

// Create collections: DB categories on movies when present, else keyword heuristics (non-homepage / legacy)
export const createCollections = (movies) => {
  const hasDbCategories = movies.some(
    (m) => Array.isArray(m.categories) && m.categories.length > 0
  );
  if (hasDbCategories) {
    return createCollectionsFromDbCategories(movies);
  }
  return createCollectionsHeuristic(movies);
};

/**
 * Homepage hero: ordered movies from admin slider, or [] to use fallback rotation.
 */
export async function fetchHomepageSliderMovies() {
  try {
    const flags = await getHomepagePublishFlags();
    if (!flags.sliderPublished) {
      return [];
    }

    const { data, error } = await supabase
      .from('homepage_slider_items')
      .select(
        `
        sort_order,
        movies (
          ${MOVIE_WITH_CATEGORIES}
        )
      `
      )
      .order('sort_order', { ascending: true });

    if (error) throw error;

    const rows = data || [];
    const out = [];
    rows.forEach((row) => {
      const m = row.movies;
      if (m && isValidYouTubeId(m.video_id)) {
        out.push(mapDbMovieToFrontend(m));
      }
    });
    return out;
  } catch (e) {
    console.warn('Homepage slider fetch failed, using fallback hero:', e);
    return [];
  }
}

// Send new movie data to Supabase (for Google Apps Script integration)
export const sendNewMovies = async (movies) => {
  try {
    const { data, error } = await supabase
      .from('movies')
      .insert(movies);
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Error sending movies to Supabase:', error);
    throw error;
  }
};

// Supabase endpoint for Google Apps Script
export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project-id.supabase.co';

const movieApi = {
  fetchMovies,
  fetchMoviesByChannel,
  sendNewMovies,
  convertSheetRowToVideo,
  getMostWatchedMovies,
  getMostRecentMovies,
  createCollections,
  fetchHomepageCollectionsFromAdmin,
  fetchHomepageSliderMovies,
  supabaseUrl
};

export default movieApi;
